// Ports of SuiteHistory/ProjectState.swift (`ProjectState`, `StateResolver`) and ProjectDiff.swift (`ProjectDiff`):
// the project as it stood at a checkpoint — the project document and each fragment read at the heads it recorded —
// or as it is now, and what changed between two of them: fragments added, cut, moved, split out of or merged into
// another (structure), each revised fragment's `TextDiff`, word totals, words by a field's value, and what changed on
// each map (`MapChange`: maps live outside the manuscript, so anywhere in the project).
//
// A checkpoint names heads for every fragment; a fragment whose heads haven't reached this device is listed in
// `missingFragments`, never guessed at (and left out on both sides of a comparison).
import type * as A from '@automerge/automerge';
import type { FragmentKind, MetadataValue } from '../contract.ts';
import { decodeHeads } from '../automerge/heads.ts';
import { checkpointHeadsIn } from '../epochs/reanchor.ts';
import { orderedFragmentIDs } from '../model/arrangement.ts';
import { MapChange, type MapContent } from '../maps/map.ts';
import { readFragment, readProject, type Arrangement, type CheckpointRecord, type FragmentRecord, type MetadataField } from '../model/documents.ts';
import { swiftCompare } from '../model/order.ts';
import { plainText, wordCount } from '../proseMarkdown.ts';
import { graphemes } from '../unicode.ts';
import { difference, inferringMoves } from './collectionDifference.ts';
import { isUnchanged, sentences, textDiff, type TextDiffResult } from './textDiff.ts';

export interface StateFragment {
  id: string;
  title: string;
  text: string;
  metadata: Record<string, MetadataValue>;
  kind: FragmentKind;
  /** A map's contents (its `text` is unused). */
  map?: MapContent;
}

/** A fragment as a state holds it. */
export const stateFragment = (id: string, f: FragmentRecord): StateFragment =>
  ({ id, title: f.title, text: f.text, metadata: f.metadata, kind: f.kind, ...(f.map ? { map: f.map } : {}) });

export interface ProjectState {
  label: string;
  arrangements: Arrangement[];
  fragments: Map<string, StateFragment>;
  fields: MetadataField[];
  /** Fragments the checkpoint names whose history hasn't arrived here yet. */
  missingFragments: string[];
}

/** `arrangement(named:)`: by name or id, else the manuscript. */
export function arrangementNamed(state: ProjectState, name?: string | null): Arrangement | undefined {
  if (name) return state.arrangements.find((a) => a.name === name || a.id === name);
  return state.arrangements.find((a) => a.role === 'manuscript');
}

const FRAGMENT = 'fragment.';

/** `StateResolver.live`: the documents as they are now. `read` may supply fragments already read (the app's project). */
export function liveState(docs: Map<string, A.Doc<unknown>>, read?: (id: string) => StateFragment | null): ProjectState {
  const project = docs.get('project');
  if (!project) throw new Error('No project document');
  const record = readProject(project);
  const fragments = new Map<string, StateFragment>();
  for (const [name, doc] of docs) {
    if (!name.startsWith(FRAGMENT)) continue;
    const id = name.slice(FRAGMENT.length);
    const known = read?.(id);
    if (known) fragments.set(id, known);
    else {
      fragments.set(id, stateFragment(id, readFragment(doc)));
    }
  }
  return { label: 'Live draft', arrangements: record.arrangements, fragments, fields: record.fields, missingFragments: [] };
}

/** `StateResolver.at`: the project as it stood at `checkpoint`. Throws when the project document lacks its heads. */
export function checkpointState(docs: Map<string, A.Doc<unknown>>, checkpoint: CheckpointRecord): ProjectState {
  const project = docs.get('project');
  if (!project) throw new Error('No project document');
  // Heads of a history since compacted are found through the compaction record (the lazy half of the Swift's remap).
  const heads = checkpointHeadsIn(checkpoint.projectHeads, project);
  if (!heads) throw new Error(`The project as it was at “${checkpoint.name}” hasn’t reached this computer yet.`);
  const past = readProject(project, heads);
  const fragments = new Map<string, StateFragment>();
  const missing: string[] = [];
  for (const [id, hex] of Object.entries(checkpoint.fragmentHeads)) {
    const doc = docs.get(FRAGMENT + id);
    try {
      decodeHeads(hex);
    } catch {
      missing.push(id);
      continue;
    }
    const at = doc ? checkpointHeadsIn(hex, doc) : null;
    if (!doc || !at) {
      missing.push(id);
      continue;
    }
    fragments.set(id, stateFragment(id, readFragment(doc, at)));
  }
  return { label: checkpoint.name, arrangements: past.arrangements, fragments, fields: past.fields, missingFragments: missing.sort(swiftCompare) };
}

export type StructuralChange =
  | { type: 'added'; id: string; at: number }
  | { type: 'cut'; id: string; from: number }
  | { type: 'moved'; id: string; from: number; to: number }
  /** `id` was split out of `source`. */
  | { type: 'split'; id: string; source: string }
  /** `id` was merged into `into`. */
  | { type: 'merged'; id: string; into: string };

export interface FragmentChange {
  id: string;
  title: string;
  diff: TextDiffResult;
}

export interface ProjectDiff {
  from: string;
  to: string;
  arrangementName: string;
  structural: StructuralChange[];
  revised: FragmentChange[];
  wordsBefore: number;
  wordsAfter: number;
  grossAdded: number;
  grossDeleted: number;
  /** Words added and deleted by a field's value (the value as stored: an option or entity id, "—" for none). */
  byField: Map<string, Map<string, { added: number; deleted: number }>>;
  missingFragments: string[];
  /** Maps changed between the two states (anywhere in the project: maps live outside the manuscript). */
  mapChanges: MapChange[];
  /** A fragment's title in either state ("Untitled" when it has none). */
  title(id: string): string;
}

export const netWords = (d: ProjectDiff) => d.wordsAfter - d.wordsBefore;

/** `counts`: fragments revised, cut, moved, added, split and merged. */
export function diffCounts(d: ProjectDiff) {
  const r = { revised: d.revised.length, cut: 0, moved: 0, added: 0, split: 0, merged: 0 };
  for (const c of d.structural) r[c.type === 'cut' ? 'cut' : c.type === 'moved' ? 'moved' : c.type === 'added' ? 'added' : c.type === 'split' ? 'split' : 'merged']++;
  return r;
}

/** `MetadataValue.displayString`. */
function displayString(v: MetadataValue): string {
  switch (v.type) {
    case 'text': return v.value;
    case 'number': return new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(v.value);
    case 'boolean': return v.value ? 'Yes' : 'No';
    case 'date': return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(v.value);
  }
}

const normalise = (s: string) => s.replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '').toLowerCase();

/** `ProjectDiff.compare`. */
export function compareStates(old: ProjectState, next: ProjectState, o: { arrangement?: string | null; groupBy?: string[] } = {}): ProjectDiff {
  const fields = o.groupBy ?? ['pov', 'status'];
  const oldArrangement = arrangementNamed(old, o.arrangement);
  const newArrangement = arrangementNamed(next, o.arrangement) ?? oldArrangement;
  const a = oldArrangement ? orderedFragmentIDs(oldArrangement) : [];
  const b = newArrangement ? orderedFragmentIDs(newArrangement) : [];

  let structural: StructuralChange[] = [];
  const added: string[] = [], cut: string[] = [];
  for (const c of inferringMoves(difference(a, b), (s) => s)) {
    if (c.type === 'insert') {
      if (c.associatedWith !== null) structural.push({ type: 'moved', id: c.element, from: c.associatedWith, to: c.offset });
      else added.push(c.element);
    } else if (c.associatedWith === null) {
      cut.push(c.element);
      structural.push({ type: 'cut', id: c.element, from: c.offset });
    }
  }
  // Moves reported by the collection diff include items displaced by neighbours; keep genuine ones only.
  structural = structural.filter((c) => c.type !== 'moved' || c.from !== c.to);

  const sentenceCache = new Map<string, Set<string>>();
  const sentenceSet = (side: string, id: string, state: ProjectState): Set<string> | null => {
    const key = side + '|' + id;
    const hit = sentenceCache.get(key);
    if (hit) return hit;
    const text = state.fragments.get(id)?.text;
    if (text === undefined) return null;
    const set = new Set(sentences(plainText(text)).map(normalise));
    sentenceCache.set(key, set);
    return set;
  };
  const bestOverlap = (mine: Set<string> | null, candidates: string[], other: (id: string) => Set<string> | null): string | null => {
    const own = [...(mine ?? [])].filter((s) => graphemes(s).length > 12);
    if (!own.length) return null;
    let best: [string, number] | null = null;
    for (const id of candidates) {
      const theirs = other(id);
      if (!theirs) continue;
      const overlap = own.filter((s) => theirs.has(s)).length / own.length;
      if (overlap >= 0.6 && overlap > (best?.[1] ?? 0)) best = [id, overlap];
    }
    return best?.[0] ?? null;
  };
  const setA = new Set(a), setB = new Set(b);
  const kept = { old: a.filter((id) => setB.has(id)), new: b.filter((id) => setA.has(id)) };
  for (const id of added) {
    const at = Math.max(0, b.indexOf(id));
    const source = bestOverlap(sentenceSet('new', id, next), kept.old, (x) => sentenceSet('old', x, old));
    structural.push(source ? { type: 'split', id, source } : { type: 'added', id, at });
  }
  for (const id of cut) {
    const into = bestOverlap(sentenceSet('old', id, old), kept.new, (x) => sentenceSet('new', x, next));
    if (into) structural.push({ type: 'merged', id, into });
  }

  const revised: FragmentChange[] = [];
  let grossAdded = 0, grossDeleted = 0;
  const byField = new Map<string, Map<string, { added: number; deleted: number }>>();
  const tally = (f: StateFragment | undefined, add: number, del: number) => {
    for (const field of fields) {
      const v = f?.metadata[field];
      const value = v ? displayString(v) : '—';
      const values = byField.get(field) ?? new Map<string, { added: number; deleted: number }>();
      const t = values.get(value) ?? { added: 0, deleted: 0 };
      values.set(value, { added: t.added + add, deleted: t.deleted + del });
      byField.set(field, values);
    }
  };
  const missing = new Set([...old.missingFragments, ...next.missingFragments]);
  for (const id of new Set([...a, ...b])) {
    if (missing.has(id)) continue;
    const before = old.fragments.get(id)?.text ?? '';
    const after = next.fragments.get(id)?.text ?? '';
    if (before === after) continue;
    const diff = textDiff(before, after);
    grossAdded += diff.wordsAdded;
    grossDeleted += diff.wordsDeleted;
    tally(next.fragments.get(id) ?? old.fragments.get(id), diff.wordsAdded, diff.wordsDeleted);
    if (setA.has(id) && setB.has(id) && !isUnchanged(diff)) revised.push({ id, title: next.fragments.get(id)?.title ?? '', diff });
  }
  const position = new Map<string, number>();
  b.forEach((id, i) => { if (!position.has(id)) position.set(id, i); });
  revised.sort((x, y) => (position.get(x.id) ?? 0) - (position.get(y.id) ?? 0));

  const titles = new Map<string, string>();
  for (const [id, f] of old.fragments) titles.set(id, f.title);
  for (const [id, f] of next.fragments) titles.set(id, f.title);
  const words = (f: StateFragment | undefined) => (f ? wordCount(f.text, f.kind) : 0);
  const mapChanges: MapChange[] = [];
  for (const id of [...new Set([...old.fragments.keys(), ...next.fragments.keys()])].filter((x) => !missing.has(x)).sort(swiftCompare)) {
    const was = old.fragments.get(id), now = next.fragments.get(id);
    if (was?.kind !== 'map' && now?.kind !== 'map') continue;
    const change = MapChange.compare(was?.map, now?.map, id, now?.title ?? was?.title ?? '');
    if (change) mapChanges.push(change);
  }
  mapChanges.sort((x, y) => swiftCompare(x.title.toLocaleLowerCase(), y.title.toLocaleLowerCase()) || swiftCompare(x.id, y.id));

  return {
    from: old.label,
    to: next.label,
    arrangementName: newArrangement?.name ?? '—',
    structural,
    revised,
    wordsBefore: a.filter((id) => !missing.has(id)).reduce((n, id) => n + words(old.fragments.get(id)), 0),
    wordsAfter: b.filter((id) => !missing.has(id)).reduce((n, id) => n + words(next.fragments.get(id)), 0),
    grossAdded,
    grossDeleted,
    byField,
    missingFragments: [...missing].sort(swiftCompare),
    mapChanges,
    title: (id) => {
      const t = titles.get(id);
      return t === undefined ? id : t || 'Untitled';
    },
  };
}

/** `ProjectDiff.markdownReport(fieldNames:)`: the summary a checkpoint comparison exports (and MCP `diff` returns). */
export function markdownReport(d: ProjectDiff, fieldNames: Record<string, string> = {}): string {
  const c = diffCounts(d);
  const net = netWords(d);
  const lines = [
    `# Changes: ${d.from} → ${d.to}`,
    '',
    `Arrangement: ${d.arrangementName}`,
    '',
    '| Measure | Count |',
    '| --- | ---: |',
    `| Fragments revised | ${c.revised} |`,
    `| Fragments added | ${c.added} |`,
    `| Fragments cut | ${c.cut} |`,
    `| Fragments moved | ${c.moved} |`,
    `| Fragments split | ${c.split} |`,
    `| Fragments merged | ${c.merged} |`,
    `| Words before | ${d.wordsBefore} |`,
    `| Words after | ${d.wordsAfter} |`,
    `| Net words | ${net >= 0 ? '+' : ''}${net} |`,
    `| Gross words added | ${d.grossAdded} |`,
    `| Gross words deleted | ${d.grossDeleted} |`,
  ];
  if (d.missingFragments.length) {
    lines.push('', `> ${d.missingFragments.length} fragment(s) are waiting for sync and were not compared.`);
  }
  if (d.structural.length) {
    lines.push('', '## Structure', '');
    for (const s of d.structural) {
      switch (s.type) {
        case 'added': lines.push(`- Added **${d.title(s.id)}** at position ${s.at + 1}`); break;
        case 'cut': lines.push(`- Cut **${d.title(s.id)}** (was position ${s.from + 1})`); break;
        case 'moved': lines.push(`- Moved **${d.title(s.id)}** from ${s.from + 1} to ${s.to + 1}`); break;
        case 'split': lines.push(`- Split **${d.title(s.id)}** out of **${d.title(s.source)}**`); break;
        case 'merged': lines.push(`- Merged **${d.title(s.id)}** into **${d.title(s.into)}**`); break;
      }
    }
  }
  if (d.revised.length) {
    lines.push('', '## Revised fragments', '', '| Fragment | Added | Deleted | Sentences moved |', '| --- | ---: | ---: | ---: |');
    for (const r of d.revised) lines.push(`| ${d.title(r.id)} | ${r.diff.wordsAdded} | ${r.diff.wordsDeleted} | ${r.diff.sentencesMoved} |`);
  }
  for (const [field, values] of [...d.byField].sort(([a], [b]) => swiftCompare(a, b))) {
    if (![...values.keys()].some((v) => v !== '—')) continue;
    lines.push('', `## By ${fieldNames[field] ?? field}`, '', '| Value | Added | Deleted |', '| --- | ---: | ---: |');
    for (const [value, t] of [...values].sort(([a], [b]) => swiftCompare(a, b))) lines.push(`| ${value} | ${t.added} | ${t.deleted} |`);
  }
  return lines.join('\n') + '\n';
}
